import { useState } from 'react';
import { ContinueButton, RequiredNote } from './common.jsx';
import { parseHeight, formatHeight, nextPart, MAX_FEET, MAX_INCHES } from '../engine/height.js';

// Free-text (single box). Used for single_text / else questions.
export function TextInput({ item, answers, setAnswer, onAdvance }) {
  const val = answers[item.id] || '';
  const incomplete = item.required && !val.trim();
  return (
    <>
      <h1>{item.label}</h1>
      <textarea
        className="ta"
        value={val}
        onChange={(e) => setAnswer(item.id, e.target.value)}
      />
      {incomplete && <RequiredNote textAnswer />}
      <div className="spacer" style={{ height: 16 }} />
      <ContinueButton onClick={onAdvance} disabled={incomplete} />
    </>
  );
}

// Short single-line value (height, weight, hours...).
export function NumberInput({ item, answers, setAnswer, onAdvance }) {
  const val = answers[item.id] || '';
  const incomplete = item.required && !String(val).trim();
  return (
    <>
      <h1>{item.label}</h1>
      <input
        className="win narrow"
        inputMode="numeric"
        placeholder={item.placeholder || ''}
        value={val}
        onChange={(e) => setAnswer(item.id, e.target.value)}
      />
      {incomplete && <RequiredNote />}
      <div className="spacer" />
      <ContinueButton onClick={onAdvance} disabled={incomplete} />
    </>
  );
}

// Height — feet and inches in their own boxes.
//
// Not a plain `num`: that gave a numeric keypad with a placeholder ("e.g. 5 ft 10")
// that keypad cannot type, so answers came back as "54", "510" and — 157 times —
// a bare "5" with the inches gone. Split in two, each box really is a number, so
// the numeric keypad is finally the right keyboard for what's being asked.
export function HeightInput({ item, answers, setAnswer, onAdvance }) {
  // Seeded once from the stored answer, then owned locally. Deriving both boxes from
  // the composite string every render would mean clearing Feet to correct it also
  // wipes Inches — the stored "5' 10\"" becomes "" the moment feet is empty — and
  // changing your feet is a normal correction, not an edge case.
  //
  // Safe to seed once: AssessmentFlow keys this screen by item id so it remounts per
  // question, and useResume gates the flow on `ready`, so saved answers are already
  // in hand before any question mounts.
  const [{ feet, inches }, setParts] = useState(() => parseHeight(answers[item.id]));

  const update = (nextFeet, nextInches) => {
    setParts({ feet: nextFeet, inches: nextInches });
    setAnswer(item.id, formatHeight(nextFeet, nextInches));
  };

  // Typing into a filled box replaces the answer rather than appending to it — see
  // nextPart. Appending is what made this screen impossible to correct.
  const onFeet = (e) => update(nextPart(feet, e.target.value, MAX_FEET), inches);
  const onInches = (e) => update(feet, nextPart(inches, e.target.value, MAX_INCHES));

  // Coming back to a filled box selects what's in it, so the next digit replaces it
  // outright and the caret can't land in front of the old one (clicking the left half
  // of "5" and typing 6 would otherwise read "65", and correcting height is the whole
  // problem here). onMouseUp is the other half of it: without preventing the default,
  // the click that follows focus drops the selection and puts the caret back.
  const selectAll = (e) => e.currentTarget.select();
  const keepSelection = (e) => e.preventDefault();

  return (
    <>
      <h1>{item.label}</h1>
      <div className="heightgrid">
        <div>
          <label className="flab" htmlFor={`${item.id}-ft`}>Feet</label>
          <input
            id={`${item.id}-ft`}
            className="win"
            inputMode="numeric"
            placeholder="5"
            value={feet}
            onChange={onFeet}
            onFocus={selectAll}
            onMouseUp={keepSelection}
          />
        </div>
        <div>
          <label className="flab" htmlFor={`${item.id}-in`}>Inches</label>
          <input
            id={`${item.id}-in`}
            className="win"
            inputMode="numeric"
            placeholder="10"
            value={inches}
            onChange={onInches}
            onFocus={selectAll}
            onMouseUp={keepSelection}
          />
        </div>
      </div>
      <div className="spacer" />
      <ContinueButton onClick={onAdvance} disabled={!feet} />
    </>
  );
}

// A native date picker opens on today and a tap-through accepts it, so a
// non-empty value is not evidence anyone chose one: 10.8% of submissions came
// back with a birth year of 2020 or later and 2% were literally the submission
// date. So check the value rather than just its presence.
//
// The first version of this bounded the field at 18 years old, which turned out
// to lock out real clients: a coach's 14-year-old could not be entered at all,
// and the family got through only by adding five years to his birthday — a wrong
// date of birth written into the record and read as fact by every coach and by
// the AI analysis downstream. The floor was only ever a proxy for "not the
// picker's default", never a decision that the NTA is adults-only, and a young
// client is exactly the person whose real age matters most to a health intake.
//
// So a young date now ASKS instead of refusing. Only a date that cannot be a
// birthday at all — today, the future, or beyond a human lifespan — is refused
// outright. The confirmation has to be ticked deliberately, which is what makes
// it different from the tap-through it is guarding against.
const CONFIRM_UNDER_AGE = 18;
const MAX_AGE_YEARS = 120;

// Local calendar day, not toISOString() — that is UTC, so for anyone behind it
// "today" would resolve to tomorrow's date and the today check would miss the
// very value the picker had just offered them.
const isoDay = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const yearsAgo = (n) => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - n);
  return isoDay(d);
};

/** Whole years old on `today`, or null if the value isn't a date. */
export function ageInYears(val, today = new Date()) {
  const [y, m, d] = String(val).split('-').map(Number);
  if (!y || !m || !d) return null;
  const hadBirthday =
    today.getMonth() + 1 > m || (today.getMonth() + 1 === m && today.getDate() >= d);
  return today.getFullYear() - y - (hadBirthday ? 0 : 1);
}

/** '' when the date is usable, else the reason to show the person. */
export function dateOfBirthProblem(val, { min } = {}) {
  if (!val) return '';
  const lo = min ?? yearsAgo(MAX_AGE_YEARS);
  const today = isoDay(new Date());
  if (val === today) return 'That’s today’s date — please enter the day you were born.';
  if (val > today) return 'That date is in the future — please enter the day you were born.';
  if (val < lo) return 'Please check the year — that date looks too far back.';
  return '';
}

/**
 * '' when nothing needs confirming, else the question to put to the person.
 *
 * A young age is a claim worth double-checking, not one worth refusing: it is
 * equally the shape of a mistyped year and of a real 14-year-old client.
 */
export function dateOfBirthConfirm(val, today = new Date()) {
  if (!val || dateOfBirthProblem(val)) return '';
  const age = ageInYears(val, today);
  if (age === null || age >= CONFIRM_UNDER_AGE) return '';
  return age < 1
    ? 'That date is less than a year ago — tick the box if it is right.'
    : `That date means an age of ${age} — tick the box if it is right.`;
}

// A day only exists for its own month (Feb 30 doesn't), and typed digits don't
// know that the way the native picker's calendar did — so round-trip through
// Date and confirm the parts survive, the standard way to catch a rollover.
function isRealCalendarDate(month, day, year) {
  const d = new Date(year, month - 1, day);
  return d.getFullYear() === year && d.getMonth() === month - 1 && d.getDate() === day;
}

/** Split a stored "YYYY-MM-DD" into the three boxes. '' where unset/unparseable. */
export function parseDob(value) {
  const [y, m, d] = String(value || '').split('-');
  return {
    month: m ? String(Number(m)) : '',
    day: d ? String(Number(d)) : '',
    year: y && y.length === 4 ? y : '',
  };
}

/**
 * "YYYY-MM-DD" once month, day and a 4-digit year are all present and form a
 * real calendar date — else ''. That follows the same convention the rest of
 * this screen already uses (empty means "not answered yet", not "wrong"), so
 * an impossible date needs no message of its own: Continue simply stays
 * disabled the same way it does for any other incomplete required field.
 *
 * ponytail: a mistyped, nonexistent date (Feb 30) reads as merely incomplete
 * rather than getting a dedicated "that date doesn't exist" message. Add one
 * if this proves confusing in practice.
 */
export function assembleDob(month, day, year) {
  const m = Number(month);
  const d = Number(day);
  const y = Number(year);
  if (!m || !d || String(year).length !== 4 || !isRealCalendarDate(m, d, y)) return '';
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

export function DateInput({ item, answers, setAnswer, onAdvance }) {
  // Seeded once from the stored answer, then owned locally — same reasoning as
  // HeightInput: deriving the boxes from the composite string every render
  // would mean clearing one box to correct it also wipes the others.
  const [{ month, day, year }, setParts] = useState(() => parseDob(answers[item.id]));
  const val = assembleDob(month, day, year);

  const update = (nextMonth, nextDay, nextYear) => {
    setParts({ month: nextMonth, day: nextDay, year: nextYear });
    setAnswer(item.id, assembleDob(nextMonth, nextDay, nextYear));
  };

  // nextPart (see height.js) reads an over-range keystroke as a fresh digit
  // instead of dropping it — without it these boxes go uneditable after the
  // first answer, exactly the height-field bug this reuses the fix for.
  const onMonth = (e) => update(nextPart(month, e.target.value, 12), day, year);
  const onDay = (e) => update(month, nextPart(day, e.target.value, 31), year);
  const onYear = (e) =>
    update(month, day, nextPart(year, e.target.value, new Date().getFullYear()));

  // Same correction pattern as the height boxes: select-on-focus so a tap
  // replaces the whole value, and keepSelection stops the click that follows
  // focus from dropping that selection back to a caret.
  const selectAll = (e) => e.currentTarget.select();
  const keepSelection = (e) => e.preventDefault();

  const min = item.min ?? yearsAgo(MAX_AGE_YEARS);
  const problem = dateOfBirthProblem(val, { min });
  const confirm = dateOfBirthConfirm(val);
  // Tied to the date it was ticked for, so changing the date asks again rather
  // than carrying a confirmation over to a value nobody looked at.
  const [confirmedFor, setConfirmedFor] = useState('');
  const confirmed = confirm && confirmedFor === val;
  const incomplete = (item.required && !val) || !!problem || (!!confirm && !confirmed);
  return (
    <>
      <h1>{item.label}</h1>
      <div className="dobgrid">
        <div>
          <label className="flab" htmlFor={`${item.id}-mm`}>Month</label>
          <input
            id={`${item.id}-mm`}
            className="win"
            inputMode="numeric"
            placeholder="MM"
            value={month}
            onChange={onMonth}
            onFocus={selectAll}
            onMouseUp={keepSelection}
          />
        </div>
        <div>
          <label className="flab" htmlFor={`${item.id}-dd`}>Day</label>
          <input
            id={`${item.id}-dd`}
            className="win"
            inputMode="numeric"
            placeholder="DD"
            value={day}
            onChange={onDay}
            onFocus={selectAll}
            onMouseUp={keepSelection}
          />
        </div>
        <div>
          <label className="flab" htmlFor={`${item.id}-yyyy`}>Year</label>
          <input
            id={`${item.id}-yyyy`}
            className="win"
            inputMode="numeric"
            placeholder="YYYY"
            value={year}
            onChange={onYear}
            onFocus={selectAll}
            onMouseUp={keepSelection}
          />
        </div>
      </div>
      {problem && <p className="reqnote">{problem}</p>}
      {!problem && confirm && (
        <label className="reqnote confirmage">
          <input
            type="checkbox"
            checked={!!confirmed}
            onChange={(e) => setConfirmedFor(e.target.checked ? val : '')}
          />
          <span>{confirm}</span>
        </label>
      )}
      {!problem && !confirm && item.required && !val && <RequiredNote />}
      <div className="spacer" />
      <ContinueButton onClick={onAdvance} disabled={incomplete} />
    </>
  );
}

// Multiple labeled inputs on one screen (e.g. first + last name). Each writes to its
// own field id.
export function FieldsInput({ item, answers, setAnswer, onAdvance }) {
  // When the item is required, every field must be filled (e.g. first + last name).
  const incomplete = item.required && item.fields.some((f) => !(answers[f.id] || '').trim());
  return (
    <>
      <h1>{item.label}</h1>
      <div className="fieldgrid">
        {item.fields.map((f) => (
          <div key={f.id}>
            <label className="flab">{f.label}</label>
            <input
              className="win"
              value={answers[f.id] || ''}
              onChange={(e) => setAnswer(f.id, e.target.value)}
            />
          </div>
        ))}
      </div>
      {incomplete && <RequiredNote />}
      <div className="spacer" />
      <ContinueButton onClick={onAdvance} disabled={incomplete} />
    </>
  );
}

// Email — captured early; creates the account server-side (wired in the accounts step).
export function EmailInput({ item, answers, setAnswer, onAdvance, busy }) {
  const value = answers[item.id] || '';
  const valid = /.+@.+\..+/.test(value);
  return (
    <>
      <h1>{item.label}</h1>
      {item.help && <p className="sub">{item.help}</p>}
      <input
        className="win"
        type="email"
        placeholder="you@example.com"
        value={value}
        onChange={(e) => setAnswer(item.id, e.target.value)}
      />
      <p className="note">🔒 We create your secure account automatically — no password required.</p>
      <div className="spacer" style={{ height: 16 }} />
      <ContinueButton label={busy ? 'Saving…' : 'Continue'} onClick={onAdvance} disabled={!valid || busy} />
    </>
  );
}
